/*
 * InteractiveSeatMap.jsx — Tripjack interactive seat picker.
 *
 * Fetches GET /api/tripjack/seatmap/{priceId}, renders the per-segment
 * seat grid, lets each passenger pick exactly one seat per segment, and
 * emits the selections via `onSelectionsChange` as:
 *
 *   { [segmentKey]: { [paxIndex]: { code, price, key, bookingSegmentId, type, ... } } }
 *
 * `segmentKey` / `key` stay UI tab keys; `bookingSegmentId` is the Tripjack
 * review segment id (`sI.id`) used on /book `ssrSeatInfos[].key`.
 *
 * Soft-fails silently when /seatmap returns no data — caller should fall
 * back to the legacy flat SSR-seat list.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, Plane, AlertCircle, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatINR = (n) =>
  Number(n || 0) === 0
    ? 'Free'
    : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const seatColor = ({ available, seat_type, price, isSelected }) => {
  if (isSelected) return 'bg-emerald-500 text-white border-emerald-600 shadow-lg ring-2 ring-emerald-300';
  if (!available) return 'bg-gray-200 text-gray-300 border-gray-300 cursor-not-allowed';
  if (seat_type === 'EMERGENCY_EXIT')
    return 'bg-rose-50 text-rose-900 border-rose-300 hover:bg-rose-100 hover:scale-110 cursor-pointer';
  if (seat_type === 'PREMIUM' || price > 0)
    return 'bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100 hover:scale-110 cursor-pointer';
  return 'bg-white text-gray-700 border-gray-300 hover:bg-emerald-50 hover:border-emerald-400 hover:scale-110 cursor-pointer';
};

const Legend = () => (
  <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] text-gray-600 mt-2 mb-3">
    <span className="flex items-center gap-1.5">
      <span className="w-4 h-4 rounded-md border border-gray-300 bg-white"></span>Free
    </span>
    <span className="flex items-center gap-1.5">
      <span className="w-4 h-4 rounded-md border border-amber-300 bg-amber-50"></span>Paid / Legroom
    </span>
    <span className="flex items-center gap-1.5">
      <span className="w-4 h-4 rounded-md border border-rose-300 bg-rose-50"></span>Emergency Exit
    </span>
    <span className="flex items-center gap-1.5">
      <span className="w-4 h-4 rounded-md border border-emerald-600 bg-emerald-500"></span>Selected
    </span>
    <span className="flex items-center gap-1.5">
      <span className="w-4 h-4 rounded-md border border-gray-300 bg-gray-200"></span>Booked
    </span>
  </div>
);

// Group flat seat list by column-letter so we can render the aircraft
// horizontally (nose left, tail right). Returns an ordered array of
// { letter, seats[] } where seats are sorted by row number ascending.
const groupSeatsByColumn = (rows) => {
  const colMap = new Map();  // letter → seat[]
  rows.forEach((row) => {
    row.seats.forEach((s) => {
      const letter = (s.column || '').toUpperCase();
      if (!letter) return;
      if (!colMap.has(letter)) colMap.set(letter, []);
      colMap.get(letter).push({ ...s, _rowNo: parseInt(row.row_no, 10) || 0 });
    });
  });
  const ordered = Array.from(colMap.entries())
    .map(([letter, seats]) => ({
      letter,
      seats: seats.sort((a, b) => a._rowNo - b._rowNo),
    }))
    .sort((a, b) => a.letter.localeCompare(b.letter));
  return ordered;
};

// Returns a sorted unique list of row numbers across all rows (for the
// horizontal X-axis labels above the aircraft).
const collectRowNos = (rows) => {
  const nos = rows.map((r) => parseInt(r.row_no, 10) || 0).filter(Boolean);
  return Array.from(new Set(nos)).sort((a, b) => a - b);
};

/**
 * Tripjack sometimes returns the same marketing leg twice (e.g. RT/combo).
 * Dedupe tabs by route + flight (+ dep when present); keep first segment key.
 * Returns redirects [duplicateKey, canonicalKey] so selections can merge.
 */
function dedupeSeatmapSegmentsForUi(rawSegments) {
  const firstKeyByRoute = new Map();
  const redirects = [];
  const out = [];
  (rawSegments || []).forEach((seg) => {
    if (!seg || typeof seg !== 'object') return;
    const from = String(seg.from || '').trim().toUpperCase();
    const to = String(seg.to || '').trim().toUpperCase();
    const flight = String(seg.flight_no || '').replace(/\s+/g, '').toUpperCase();
    const dep = String(seg.dep_time ?? seg.dt ?? seg.std ?? seg.departure ?? '').trim().slice(0, 16);
    let routeKey = '';
    if (from && to && flight) {
      routeKey = dep ? `${from}|${to}|${flight}|${dep}` : `${from}|${to}|${flight}`;
    } else {
      routeKey = `key:${String(seg.key || '')}`;
    }
    if (firstKeyByRoute.has(routeKey)) {
      const keepKey = firstKeyByRoute.get(routeKey);
      if (keepKey && seg.key && seg.key !== keepKey) redirects.push([seg.key, keepKey]);
      return;
    }
    firstKeyByRoute.set(routeKey, seg.key);
    out.push(seg);
  });
  return { segments: out, redirects };
}

const InteractiveSeatMap = ({
  priceId,
  /** Tripjack Air Review `bookingId` — when set, GET /seatmap skips a second /review (PDF). */
  tripjackBookingId = null,
  passengers = [],
  segments: segmentInfo = [],
  onSelectionsChange,
  /** When seat map loads, parent receives enriched `segmentsForUI` (same order as tabs) for ancillary SSR grouping. */
  onSegmentsNormalized = null,
}) => {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [data, setData] = useState(null);
  const [activeSegmentKey, setActiveSegmentKey] = useState(null);
  const [activePaxIdx, setActivePaxIdx] = useState(0);
  const [selections, setSelections] = useState({});  // {segKey: {paxIdx: seat}}
  const [errAction, setErrAction] = useState(null);
  const [hoverSeat, setHoverSeat] = useState(null);  // {code, x, y, label}

  const seatmapUrl = useMemo(() => {
    if (!priceId) return '';
    return tripjackBookingId
      ? `${BACKEND_URL}/api/tripjack/seatmap/${encodeURIComponent(priceId)}?bookingId=${encodeURIComponent(tripjackBookingId)}`
      : `${BACKEND_URL}/api/tripjack/seatmap/${encodeURIComponent(priceId)}`;
  }, [priceId, tripjackBookingId]);

  useEffect(() => {
    if (!seatmapUrl) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    setErrAction(null);
    axios
      .get(seatmapUrl)
      .then((resp) => {
        if (cancelled) return;
        const norm = resp.data?.normalized;
        if (resp.data?.success && norm?.segments?.length) {
          setData(norm);
          setActiveSegmentKey(norm.segments[0].key);
        } else {
          setErr(resp.data?.message || 'Seat map not available for this fare.');
          setErrAction(resp.data?.action || null);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e.response?.data?.detail || e.message || 'Could not load seat map.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [seatmapUrl]);

  // Build an index of caller-supplied segment metadata so we can enrich
  // empty `from/to/flight_no` (Tripjack /seat doesn't echo per-segment
  // airport codes — they live in the /review tripInfos.sI[]).
  const segmentMeta = useMemo(() => {
    const list = (segmentInfo || []).map((s) => ({
      from: s.from || s.fromAirport || s.origin || s.da?.code || '',
      to: s.to || s.toAirport || s.destination || s.aa?.code || '',
      flight_no: (s.flightNumber || s.flight_no || s.fN || '').toString(),
      airline: s.airlineCode || s.al?.code || s.airline || '',
    }));
    return list;
  }, [segmentInfo]);

  // Enriched segments: backend key + UI-fillable from/to/flightNo via order
  const { segmentsForUI, segmentRedirects, segmentRedirectsKey } = useMemo(() => {
    if (!data?.segments) {
      return { segmentsForUI: [], segmentRedirects: [], segmentRedirectsKey: '' };
    }
    const raw = data.segments.map((seg, i) => {
      const meta = segmentMeta[i] || {};
      const bookingSegmentId =
        seg.bookingSegmentId || seg.segmentId || seg.id || seg.key;
      return {
        ...seg,
        from: seg.from || meta.from || '',
        to: seg.to || meta.to || '',
        flight_no: seg.flight_no || (meta.airline && meta.flight_no
          ? `${meta.airline}-${meta.flight_no}`
          : meta.flight_no || ''),
        bookingSegmentId,
      };
    });
    const { segments, redirects } = dedupeSeatmapSegmentsForUi(raw);
    const key = redirects.map(([a, b]) => `${a}>${b}`).sort().join('|');
    return { segmentsForUI: segments, segmentRedirects: redirects, segmentRedirectsKey: key };
  }, [data, segmentMeta]);

  const segmentRedirectsRef = useRef(segmentRedirects);
  segmentRedirectsRef.current = segmentRedirects;

  useEffect(() => {
    if (!segmentRedirectsKey) return;
    const pairs = segmentRedirectsRef.current;
    if (!pairs.length) return;
    setSelections((prev) => {
      let changed = false;
      const next = { ...prev };
      pairs.forEach(([fromKey, toKey]) => {
        if (!fromKey || !toKey || !prev[fromKey]) return;
        const merged = { ...(next[toKey] || {}) };
        Object.entries(prev[fromKey]).forEach(([paxIdx, seat]) => {
          if (seat?.code && !merged[paxIdx]?.code) merged[paxIdx] = seat;
        });
        next[toKey] = merged;
        delete next[fromKey];
        changed = true;
      });
      if (!changed) return prev;
      onSelectionsChange?.(next);
      return next;
    });
  }, [segmentRedirectsKey, onSelectionsChange]);

  const segmentTabKeysSig = useMemo(
    () => segmentsForUI.map((s) => s.key).join('|'),
    [segmentsForUI],
  );

  useEffect(() => {
    if (!segmentsForUI.length) return;
    setActiveSegmentKey((prev) => {
      if (segmentsForUI.some((s) => s.key === prev)) return prev;
      const hit = segmentRedirectsRef.current.find(([fromK]) => fromK === prev);
      if (hit) return hit[1];
      return segmentsForUI[0].key;
    });
  }, [segmentTabKeysSig, segmentsForUI, segmentRedirectsKey]);

  useEffect(() => {
    if (segmentsForUI.length > 0 && typeof onSegmentsNormalized === 'function') {
      onSegmentsNormalized(segmentsForUI);
    }
  }, [segmentsForUI, onSegmentsNormalized]);

  const activeSegment = useMemo(
    () => segmentsForUI.find((s) => s.key === activeSegmentKey) || null,
    [segmentsForUI, activeSegmentKey]
  );

  const segSelections = selections[activeSegmentKey] || {};

  const handleSeatClick = (seat) => {
    if (!seat.available) return;
    const conflictingPax = Object.entries(segSelections).find(
      ([idx, s]) => s?.code === seat.code && Number(idx) !== activePaxIdx
    );
    if (conflictingPax) return;

    // Stamp a human-readable segment label on the selection so the price
    // summary can render "Seat 12C (Hasan) · BOM → DEL".
    const segLabel = activeSegment?.from && activeSegment?.to
      ? `${activeSegment.from} → ${activeSegment.to}`
      : '';

    const next = {
      ...selections,
      [activeSegmentKey]: {
        ...segSelections,
        [activePaxIdx]: {
          code: seat.code,
          price: seat.price,
          key: activeSegmentKey,
          bookingSegmentId:
            activeSegment?.bookingSegmentId || activeSegment?.key,
          type: seat.type,
          segmentLabel: segLabel,
          flightNo: activeSegment?.flight_no || '',
        },
      },
    };
    setSelections(next);
    onSelectionsChange?.(next);

    const nextIdx = passengers.findIndex(
      (_, i) => i !== activePaxIdx && !next[activeSegmentKey]?.[i]
    );
    if (nextIdx !== -1) setActivePaxIdx(nextIdx);
  };

  if (!priceId) return null;

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-8 flex items-center justify-center" data-testid="seatmap-loading">
        <Loader2 className="w-5 h-5 text-emerald-600 animate-spin" />
        <span className="ml-2 text-sm text-gray-600">Loading seat map…</span>
      </div>
    );
  }

  if (err || !data || data.segments.length === 0) {
    const isFareLimitation = errAction === 'skip_seat';
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-2" data-testid="seatmap-error">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-amber-900">
            {isFareLimitation
              ? 'Seat selection not included in this fare'
              : 'Interactive seat map unavailable'}
          </p>
          <p className="text-xs text-amber-800 mt-1">
            {err || 'This fare does not offer interactive seat selection. You can still pick from the seat options below or choose your seat at check-in.'}
          </p>
          {err && !isFareLimitation && (
            <button
              type="button"
              onClick={() => {
                setErr(null);
                setErrAction(null);
                setLoading(true);
                axios
                  .get(seatmapUrl)
                  .then((resp) => {
                    const norm = resp.data?.normalized;
                    if (resp.data?.success && norm?.segments?.length) {
                      setData(norm);
                      setActiveSegmentKey(norm.segments[0].key);
                    } else {
                      setErr(resp.data?.message || 'Seat map not available for this fare.');
                      setErrAction(resp.data?.action || null);
                    }
                  })
                  .catch((e) =>
                    setErr(e.response?.data?.detail || e.message || 'Could not load seat map.')
                  )
                  .finally(() => setLoading(false));
              }}
              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-900 hover:text-amber-700"
              data-testid="seatmap-retry"
            >
              ↻ Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md p-4 sm:p-6" data-testid="seatmap-panel">
      {/* Card header — matches the styling of sibling Baggage/Meals cards */}
      <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
        <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
          <svg
            className="w-6 h-6 text-emerald-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
          </svg>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-gray-800">Pick Your Seat</h3>
            <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
              Interactive
            </span>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Choose where each traveller sits — hover any seat to preview its price.
          </p>
        </div>
      </div>

      {/* Segment selector */}
      {segmentsForUI.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="seatmap-tabs">
          {segmentsForUI.map((seg, idx) => {
            const label = seg.from && seg.to
              ? `${seg.from} → ${seg.to}`
              : `Flight ${idx + 1}`;
            const picked = (selections[seg.key] || {});
            const pickedCount = Object.values(picked).filter((s) => s?.code).length;
            return (
              <button
                key={seg.key}
                type="button"
                onClick={() => {
                  setActiveSegmentKey(seg.key);
                  setActivePaxIdx(0);
                }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition ${
                  seg.key === activeSegmentKey
                    ? 'bg-[#1B375B] text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid={`seatmap-tab-${seg.key}`}
              >
                <Plane className="w-3 h-3" />
                {label}
                {seg.flight_no && (
                  <span className={`opacity-70 ${seg.key === activeSegmentKey ? 'text-white/80' : ''}`}>
                    · {seg.flight_no}
                  </span>
                )}
                {pickedCount > 0 && (
                  <span className={`ml-1 text-[10px] px-1.5 rounded-full ${
                    seg.key === activeSegmentKey ? 'bg-white/20' : 'bg-emerald-100 text-emerald-700'
                  }`}>
                    {pickedCount}/{passengers.length}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Active-segment header — always visible (even for 1-segment trips) so
          the user knows which sector their picks apply to. */}
      {activeSegment && (
        <div
          className="flex items-center gap-2 mb-3 text-xs text-gray-600 bg-gradient-to-r from-emerald-50 to-blue-50 px-3 py-2 rounded-lg border border-emerald-100"
          data-testid="seatmap-active-header"
        >
          <Plane className="w-3.5 h-3.5 text-emerald-700" />
          <span className="font-bold text-gray-800">
            {activeSegment.from && activeSegment.to
              ? `${activeSegment.from} → ${activeSegment.to}`
              : 'Selecting seats'}
          </span>
          {activeSegment.flight_no && (
            <span className="text-gray-500">· {activeSegment.flight_no}</span>
          )}
          <span className="ml-auto text-[10px] text-gray-500">
            {activeSegment.available_seats}/{activeSegment.total_seats} seats available
          </span>
        </div>
      )}

      {/* Audit §7 L10 — Surface supplier seat-map `nt` notes (e.g. "Seats
          will be assigned at check-in"). Only shown when present. */}
      {activeSegment && activeSegment.notes ? (
        <div
          className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900"
          data-testid="seatmap-segment-notes"
        >
          <span className="font-semibold">Note:</span> {activeSegment.notes}
        </div>
      ) : null}

      {/* Active passenger picker */}
      <div className="flex flex-wrap gap-2 mb-3">
        {passengers.map((p, i) => {
          const taken = segSelections[i];
          const isActive = i === activePaxIdx;
          return (
            <button
              key={i}
              type="button"
              onClick={() => setActivePaxIdx(i)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1 border transition ${
                isActive
                  ? 'bg-emerald-600 text-white border-emerald-700'
                  : taken
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
              }`}
              data-testid={`seatmap-pax-${i}`}
            >
              <User className="w-3 h-3" />
              {p.firstName || `Pax ${i + 1}`}
              {taken && <span className="ml-1 opacity-90">· {taken.code}</span>}
            </button>
          );
        })}
      </div>

      <Legend />

      {/* ── Horizontal aircraft seat map ──
          The visible cabin viewport has a CAPPED width (640px, centered
          inside the card via mx-auto) so the aircraft always overflows
          and the user sees the horizontal scroll bar. The inner aircraft
          uses tight natural seat sizes and never stretches. */}
      <div className="relative mx-auto" style={{ maxWidth: '640px' }}>
        {/* Right-edge fade — visual hint that scroll exists */}
        <div
          className="pointer-events-none absolute right-0 top-12 bottom-12 w-10 bg-gradient-to-l from-sky-50 to-transparent z-10 rounded-r-2xl"
          aria-hidden="true"
        />
        {/* Left-edge fade */}
        <div
          className="pointer-events-none absolute left-0 top-12 bottom-12 w-6 bg-gradient-to-r from-sky-50 to-transparent z-10 rounded-l-2xl"
          aria-hidden="true"
        />
        <div
          className="bg-gradient-to-br from-sky-50 via-blue-50 to-emerald-50 rounded-2xl p-3 sm:p-4 overflow-x-auto relative border border-blue-100 seatmap-scroll"
          data-testid="seatmap-grid"
        >
        {/* Floating hover-tooltip — visible price + position */}
        {hoverSeat && (
          <div
            className="pointer-events-none absolute z-20 bg-gray-900 text-white text-[10px] font-semibold px-2.5 py-1.5 rounded-lg shadow-xl whitespace-nowrap"
            style={{
              left: Math.max(4, hoverSeat.x - 60),
              top: Math.max(4, hoverSeat.y - 44),
            }}
            data-testid="seatmap-hover-tooltip"
          >
            <div className="flex items-center gap-1.5">
              <span className="text-emerald-300 font-bold">{hoverSeat.code}</span>
              <span className="opacity-50">·</span>
              <span className="capitalize text-blue-200">{hoverSeat.type}</span>
              <span className="opacity-50">·</span>
              <span className={hoverSeat.price > 0 ? 'text-amber-300' : 'text-emerald-300'}>
                {hoverSeat.price > 0 ? formatINR(hoverSeat.price) : 'Free'}
              </span>
            </div>
            {hoverSeat.note && (
              <div className="text-[9px] opacity-80 mt-0.5 italic">{hoverSeat.note}</div>
            )}
            {/* Tooltip arrow */}
            <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-900" />
          </div>
        )}

        {activeSegment.rows.length > 0 && (() => {
          const columns = groupSeatsByColumn(activeSegment.rows);
          if (columns.length === 0) return null;
          const rowNos = collectRowNos(activeSegment.rows);

          // Detect the aisle position: insert gap before the column that
          // sits closest to the middle in alphabet order (e.g. 6-abreast
          // ABC|DEF → aisle between C and D; 4-abreast AB|CD → between B
          // and C). Falls back to mid-index for unusual layouts.
          const aisleAfterIdx = Math.floor(columns.length / 2) - 1;

          // Map seat lookup key (col-letter, row-no) → seat object
          const seatByKey = new Map();
          columns.forEach((c) =>
            c.seats.forEach((s) => seatByKey.set(`${c.letter}-${s._rowNo}`, s))
          );

          return (
            <div className="w-max">
              {/* Aircraft nose hint — full width of the cabin */}
              <div className="flex items-center gap-2 mb-1.5 text-[9px] sm:text-[10px] font-semibold text-blue-800/70 uppercase tracking-wider">
                <span className="text-sm">✈</span>
                <span>Front of plane</span>
                <span className="border-t border-dashed border-blue-300 w-16 inline-block" />
              </div>

              {/* Row-number header strip — aligned with the columns below */}
              <div className="flex items-center gap-[2px]">
                <div className="w-5 sm:w-6 shrink-0" />
                {rowNos.map((rn) => (
                  <div
                    key={`hdr-${rn}`}
                    className="w-7 sm:w-8 h-4 flex items-center justify-center text-[9px] font-bold text-blue-900/60"
                  >
                    {rn}
                  </div>
                ))}
              </div>

              {/* One <div> per column letter — each row left-to-right is
                  the same seat letter across all aircraft rows. */}
              {columns.map((col, ci) => (
                <React.Fragment key={`col-${col.letter}`}>
                  <div className="flex items-center gap-[2px]">
                    {/* Column-letter label on the left */}
                    <div className="w-5 sm:w-6 shrink-0 text-center text-[9px] sm:text-[10px] font-bold text-blue-900/70">
                      {col.letter}
                    </div>
                    {rowNos.map((rn) => {
                      const seat = seatByKey.get(`${col.letter}-${rn}`);
                      if (!seat) {
                        return (
                          <div
                            key={`empty-${col.letter}-${rn}`}
                            className="w-7 h-7 sm:w-8 sm:h-8 shrink-0"
                          />
                        );
                      }
                      const isSelected =
                        segSelections[activePaxIdx]?.code === seat.code;
                      const takenByOther = Object.entries(segSelections).some(
                        ([idx, s]) => s?.code === seat.code && Number(idx) !== activePaxIdx
                      );
                      const onEnter = (e) => {
                        const r = e.currentTarget.getBoundingClientRect();
                        const grid = e.currentTarget.closest('[data-testid="seatmap-grid"]');
                        const gr = grid?.getBoundingClientRect() || { left: 0, top: 0 };
                        setHoverSeat({
                          code: seat.code,
                          type: seat.type,
                          price: seat.price,
                          x: r.left - gr.left + r.width / 2 + (grid?.scrollLeft || 0),
                          y: r.top - gr.top,
                          note: !seat.available
                            ? 'Unavailable'
                            : takenByOther
                              ? 'Taken by another passenger'
                              : seat.seat_type === 'PREMIUM'
                                ? 'Extra legroom'
                                : seat.seat_type === 'EMERGENCY_EXIT'
                                  ? 'Emergency exit row'
                                  : null,
                        });
                      };
                      return (
                        <button
                          key={seat.code}
                          type="button"
                          onClick={() => handleSeatClick(seat)}
                          onMouseEnter={onEnter}
                          onFocus={onEnter}
                          onMouseLeave={() => setHoverSeat(null)}
                          onBlur={() => setHoverSeat(null)}
                          disabled={!seat.available || takenByOther}
                          aria-label={`Seat ${seat.code}, ${seat.type}, ${
                            seat.price > 0 ? `₹${seat.price}` : 'free'
                          }${!seat.available ? ', unavailable' : ''}`}
                          className={`w-7 h-7 sm:w-8 sm:h-8 shrink-0 rounded border text-[8px] sm:text-[9px] font-bold transition-transform duration-150 relative flex items-center justify-center ${seatColor(
                            {
                              available: seat.available && !takenByOther,
                              seat_type: seat.seat_type,
                              price: seat.price,
                              isSelected,
                            }
                          )}`}
                          data-testid={`seat-${seat.code}`}
                        >
                          {isSelected ? (
                            <span className="text-sm">✓</span>
                          ) : (
                            seat.code.replace(/[^A-Z]/gi, '') || seat.code
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {/* Aisle gap line between left and right banks — uses
                      a single dashed strip (no flex-1, so the parent w-max
                      container stays at natural seat-grid width). */}
                  {ci === aisleAfterIdx && (
                    <div className="flex items-center gap-2 my-1 px-2 justify-center">
                      <span className="border-t border-dashed border-blue-300/60 w-12 inline-block" />
                      <span className="text-[8px] text-blue-400 italic">aisle</span>
                      <span className="border-t border-dashed border-blue-300/60 w-12 inline-block" />
                    </div>
                  )}
                </React.Fragment>
              ))}

              {/* Aircraft tail hint — fixed-width dashed strip (no flex-1) */}
              <div className="flex items-center gap-2 mt-1.5 text-[9px] sm:text-[10px] font-semibold text-blue-800/70 uppercase tracking-wider justify-end">
                <span className="border-t border-dashed border-blue-300 w-16 inline-block" />
                <span>Tail</span>
                <span className="text-sm rotate-180">✈</span>
              </div>
            </div>
          );
        })()}

        {activeSegment.rows.length === 0 && (
          <p className="text-center text-xs text-gray-500 py-6">
            No seats published by the airline for this segment.
          </p>
        )}
        </div>
        {/* Scroll hint chip — shown only on desktop */}
        <p className="text-[10px] text-gray-500 italic text-center mt-1 flex items-center justify-center gap-1">
          <span aria-hidden="true">←</span>
          <span>swipe / scroll horizontally to see the full cabin</span>
          <span aria-hidden="true">→</span>
        </p>
      </div>

      {/* Persistent picks summary — quick visual confirmation */}
      {Object.values(selections).some(
        (sp) => sp && Object.values(sp).some((s) => s?.code)
      ) && (
        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="seatmap-picks-summary">
          <p className="text-[11px] font-semibold text-emerald-900 mb-1.5">
            Your seat picks
          </p>
          <div className="space-y-1">
            {segmentsForUI.flatMap((seg) =>
              Object.entries(selections[seg.key] || {}).map(([paxIdx, s]) => {
                if (!s?.code) return null;
                const pax = passengers[paxIdx];
                const segLabel = seg.from && seg.to ? `${seg.from} → ${seg.to}` : `Flight ${seg.key}`;
                return (
                  <div
                    key={`pick-${seg.key}-${paxIdx}`}
                    className="flex items-center justify-between text-[11px] text-emerald-900"
                  >
                    <span>
                      <span className="font-semibold">{pax?.firstName || `Pax ${Number(paxIdx) + 1}`}</span>
                      <span className="opacity-70"> · {segLabel}</span>
                      <span className="ml-1 px-1.5 bg-emerald-100 rounded font-bold">{s.code}</span>
                    </span>
                    <span className="font-bold">
                      {Number(s.price || 0) > 0 ? `+${formatINR(s.price)}` : 'Free'}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      <p className="text-[11px] text-gray-500 mt-3 leading-snug">
        <span className="font-semibold">How to use:</span> Pick a passenger
        chip, then tap a seat. Hover to preview the price. The plane is
        shown from above — front of cabin on the left, tail on the right.
      </p>
    </div>
  );
};

export default InteractiveSeatMap;
